import { createContext, useContext, useState, type ReactNode } from "react";
import { MessageCheck } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { useToast, TOAST_TRANSITION_DELAY } from "@acto/taskpane/field-sales-2/components/primitives/Toast";
import { CreateTodoSheet } from "./CreateTodoSheet";

const CreateTodoContext = createContext<(() => void) | null>(null);

/**
 * Opens the shared create-todo sheet. Must be called from within a {@link CreateTodoFlowProvider}.
 *
 * @throws If no provider is mounted in the tree.
 */
export function useOpenCreateTodo(): () => void {
  const open = useContext(CreateTodoContext);
  if (!open) throw new Error("useOpenCreateTodo must be used within a CreateTodoFlowProvider");

  return open;
}

interface CreateTodoFlowProviderProps {
  /** The customer the todo is created for. The sheet stays unmounted if no customer is provided. */
  clientId?: string;
  children: ReactNode;
}

/**
 * Owns the create-todo flow (sheet + success toast) once for the customer subtree. Any descendant
 * triggers it via {@link useOpenCreateTodo}.
 */
export function CreateTodoFlowProvider({ clientId, children }: CreateTodoFlowProviderProps) {
  const t = useTranslations("fieldSalesTwo.todoCreate");
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const openCreateTodo = () => setIsOpen(true);

  return (
    <CreateTodoContext.Provider value={openCreateTodo}>
      {children}
      {clientId && (
        <CreateTodoSheet
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          clientId={clientId}
          onCreated={() =>
            showToast({
              message: t("successToast"),
              icon: <MessageCheck aria-hidden="true" />,
              initialDelay: TOAST_TRANSITION_DELAY,
            })
          }
        />
      )}
    </CreateTodoContext.Provider>
  );
}
